import type {
  FiatStepResponse,
  FlowState,
  Instructions,
  Surface,
} from '@fun-xyz/fiat-contract/types'

/** Stands in for a scrubbed value so a log still shows the field was present. */
export const REDACTED = '[redacted]'

/**
 * Returns a copy with session tokens and payment instructions scrubbed, safe
 * to log. State kind, phase, status and fee ids survive — that is the part
 * worth logging.
 *
 * Switches on the contract's unions rather than matching key names, so a new
 * surface or instruction kind fails to compile instead of leaking.
 */
export function redactSensitive<T extends Pick<FiatStepResponse, 'state'>>(
  envelope: T,
): T {
  return { ...envelope, state: redactState(envelope.state) }
}

function redactState(state: FlowState): FlowState {
  // Both carriers, independently: today no state has a surface AND
  // instructions (they are separate PAYMENT phases), but an either/or would
  // leak the second the contract adds one that does.
  let redacted = state
  if ('surface' in redacted && redacted.surface !== undefined) {
    redacted = { ...redacted, surface: redactSurface(redacted.surface) }
  }
  if ('instructions' in redacted) {
    redacted = {
      ...redacted,
      instructions: redactInstructions(redacted.instructions),
    }
  }
  return redacted
}

function redactSurface(surface: Surface): Surface {
  return {
    ...surface,
    params: Object.fromEntries(
      Object.keys(surface.params).map((key) => [key, REDACTED]),
    ),
  }
}

function redactInstructions(instructions: Instructions): Instructions {
  switch (instructions.kind) {
    case 'BANK_FIELDS':
      // Ids and labels are the useful half — they say which fields the user was
      // shown without saying whose account they name.
      return {
        ...instructions,
        fields: instructions.fields.map((field) => ({
          ...field,
          value: REDACTED,
        })),
      }
    case 'REFERENCE':
      return { ...instructions, code: REDACTED }
    case 'DEEPLINK':
    case 'QR_IMAGE':
      return { ...instructions, url: withoutQuery(instructions.url) }
  }
}

/**
 * Keeps the destination, drops everything after it: provider URLs carry the
 * session in the query string or fragment.
 *
 * Hand-parsed rather than via `URL` — Hermes ships an incomplete implementation,
 * and this is a substring either way.
 */
function withoutQuery(url: string): string {
  const cut = url.search(/[?#]/)
  if (cut === -1) {
    return url
  }
  // Separator kept so the log says which part was dropped.
  return `${url.slice(0, cut + 1)}${REDACTED}`
}
